import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { optimizeImageForUpload } from "@/lib/image-upload";
import { getFunctionErrorDetails, invokeWithSessionRetry } from "@/lib/invoke-with-session-retry";
import { getSafeRandomId } from "@/lib/random-id";

export type BackgroundUploadCompressionMode = "none" | "standard";
export type BackgroundUploadJobStatus = "queued" | "processing" | "failed" | "cancelled";

export type BackgroundUploadJob = {
  id: string;
  libraryId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  compressionMode: BackgroundUploadCompressionMode;
  status: BackgroundUploadJobStatus;
  progress: number;
  createdAt: string;
  updatedAt: string;
  attemptCount: number;
  errorMessage: string | null;
};

type StoredBackgroundUploadJob = BackgroundUploadJob & {
  file: File;
};

type QueueEventDetail = {
  jobs: BackgroundUploadJob[];
  completedJobId?: string;
  failedJobId?: string;
};

const DB_NAME = "athr-background-upload-queue";
const STORE_NAME = "jobs";
const DB_VERSION = 1;
const UPLOAD_QUEUE_EVENT = "athr-background-upload-queue-updated";
const BACKGROUND_UPLOAD_SYNC_TAG = "athr-background-upload-sync";
const SERVICE_WORKER_PROCESS_UPLOADS_MESSAGE = "athr-process-background-uploads";

let activeProcessor: Promise<void> | null = null;
const activeUploadXhrMap = new Map<string, XMLHttpRequest>();

// In-memory fallback for IndexedDB (e.g. Firefox Private Browsing)
let memoryStore: Map<string, StoredBackgroundUploadJob> | null = null;
let isIdbAvailable = true;

const getMemoryStore = () => {
  if (!memoryStore) memoryStore = new Map();
  return memoryStore;
};

const registerBackgroundSync = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  try {
    // Firefox can hang on .ready if SW isn't active or blocked. 
    // We use a timeout to fall back to postMessage gracefully.
    const swReady = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration>((_, reject) => 
        setTimeout(() => reject(new Error("SW ready timeout")), 1200)
      )
    ]);

    if (swReady && "sync" in swReady) {
      await swReady.sync.register(BACKGROUND_UPLOAD_SYNC_TAG);
      return;
    }
  } catch (err) {
    // Fall through to best-effort client wakeup if SW times out or fails.
    console.debug("[BackgroundUpload] Sync registration skipped or timed out:", err);
  }

  // Fallback: Notify the controller directly if available
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: SERVICE_WORKER_PROCESS_UPLOADS_MESSAGE,
      reason: "queue-updated",
    });
  }
};

const openQueueDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    try {
      if (!window.indexedDB) {
        isIdbAvailable = false;
        return reject(new Error("IndexedDB not supported"));
      }
      
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        isIdbAvailable = false;
        reject(request.error ?? new Error("Failed to open upload queue database"));
      };
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };
    } catch (err) {
      isIdbAvailable = false;
      reject(err);
    }
  });

const readWriteTransaction = async <T>(
  mode: IDBTransactionMode,
  handler: (store: any) => Promise<T> | T,
) => {
  if (!isIdbAvailable) {
    const store = getMemoryStore();
    const result = await handler({
      get: (id: string) => ({ onsuccess: null, result: store.get(id) } as any),
      put: (job: StoredBackgroundUploadJob) => {
        store.set(job.id, job);
        return { onsuccess: null } as any;
      },
      delete: (id: string) => {
        store.delete(id);
        return { onsuccess: null } as any;
      },
      getAll: () => ({ onsuccess: null, result: Array.from(store.values()) } as any),
    });
    return result;
  }

  let db: IDBDatabase | null = null;
  try {
    db = await openQueueDb();
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = await handler(store);

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Upload queue transaction failed"));
      tx.onabort = () => reject(tx.error ?? new Error("Upload queue transaction aborted"));
    });

    return result;
  } catch (err) {
    console.warn("[BackgroundUpload] IDB failed, switching to memory fallback:", err);
    isIdbAvailable = false;
    return readWriteTransaction(mode, handler);
  } finally {
    if (db) db.close();
  }
};

const requestToPromise = <T>(request: IDBRequest<T>) => {
  if (!isIdbAvailable) return Promise.resolve(request.result);
  
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Upload queue request failed"));
  });
};

const sortJobs = (jobs: BackgroundUploadJob[]) =>
  [...jobs].sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());

const toPublicJob = (job: StoredBackgroundUploadJob): BackgroundUploadJob => ({
  id: job.id,
  libraryId: job.libraryId,
  fileName: job.fileName,
  mimeType: job.mimeType,
  sizeBytes: job.sizeBytes,
  compressionMode: job.compressionMode,
  status: job.status,
  progress: job.progress,
  createdAt: job.createdAt,
  updatedAt: job.updatedAt,
  attemptCount: job.attemptCount,
  errorMessage: job.errorMessage,
});

const listStoredJobs = async () =>
  readWriteTransaction("readonly", async (store) => {
    const jobs = (await requestToPromise(store.getAll())) as StoredBackgroundUploadJob[];
    return jobs.sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
  });

const listPublicJobs = async () => sortJobs((await listStoredJobs()).map(toPublicJob));

const emitQueueUpdate = async (detail: Omit<QueueEventDetail, "jobs"> = {}) => {
  if (typeof window === "undefined") return;
  const jobs = await listPublicJobs();
  window.dispatchEvent(new CustomEvent<QueueEventDetail>(UPLOAD_QUEUE_EVENT, {
    detail: {
      jobs,
      ...detail,
    },
  }));
};

const putStoredJob = async (job: StoredBackgroundUploadJob) => {
  await readWriteTransaction("readwrite", (store) => requestToPromise(store.put(job)));
};

const deleteStoredJob = async (jobId: string) => {
  await readWriteTransaction("readwrite", (store) => requestToPromise(store.delete(jobId)));
};

const getStoredJob = async (jobId: string) =>
  readWriteTransaction("readonly", async (store) => {
    const result = await requestToPromise(store.get(jobId));
    return (result as StoredBackgroundUploadJob | undefined) ?? null;
  });

const updateStoredJob = async (
  jobId: string,
  updater: (job: StoredBackgroundUploadJob) => StoredBackgroundUploadJob,
) => {
  const current = await getStoredJob(jobId);
  if (!current) return null;
  const next = updater(current);
  await putStoredJob(next);
  return next;
};

const calculateFileHash = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
};

const getFileSignatureBase64 = async (file: File): Promise<string> => {
  const headerBytes = await file.slice(0, 16).arrayBuffer();
  const headerArray = new Uint8Array(headerBytes);
  let binary = "";
  headerArray.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const EXT_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
};

const normalizeUploadFileName = (file: File): string => {
  const trimmed = file.name.trim();
  if (trimmed.includes(".")) return trimmed;
  const fallbackExt = EXT_BY_MIME[file.type.toLowerCase()];
  return fallbackExt ? `${trimmed || "upload"}.${fallbackExt}` : trimmed || "upload.bin";
};

const uploadWithProgress = (
  jobId: string,
  uploadUrl: string,
  file: File | Blob,
  contentType: string,
  onProgress: (progress: number) => void,
) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    activeUploadXhrMap.set(jobId, xhr);

    xhr.open("PUT", uploadUrl, true);
    
    // For R2 pre-signed URLs, the Content-Type must match exactly what was signed.
    // We ensure we only set it if provided, otherwise we let the browser or R2 handle it.
    if (contentType) {
      xhr.setRequestHeader("Content-Type", contentType);
    }

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      const progress = Math.min(99, Math.max(0, Math.round((event.loaded / event.total) * 100)));
      onProgress(progress);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`Failed to upload to R2: ${xhr.status} ${xhr.statusText}`));
    };

    xhr.onerror = () => reject(new Error("Network error while uploading file"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));

    xhr.send(file);
  }).finally(() => {
    activeUploadXhrMap.delete(jobId);
  });

const processSingleUpload = async (job: StoredBackgroundUploadJob) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("No active session. Please sign in again to resume uploads.");
  }

  const isImage = job.mimeType.startsWith("image/");
  const uploadSource = isImage ? await optimizeImageForUpload(job.file) : job.file;
  const fileHash = await calculateFileHash(uploadSource);
  const fileSignature = await getFileSignatureBase64(uploadSource);
  const normalizedFileName = normalizeUploadFileName(uploadSource);

  await updateStoredJob(job.id, (current) => ({
    ...current,
    progress: 14,
    updatedAt: new Date().toISOString(),
  }));
  await emitQueueUpdate();

  const { data, error: invokeError } = await invokeWithSessionRetry<{
    newKey?: string;
    alreadyExists?: boolean;
    existingKey?: string;
    uploadUrl?: string;
    contentType?: string;
  }>("r2-upload", {
    body: {
      fileName: normalizedFileName,
      fileType: uploadSource.type,
      fileSize: uploadSource.size,
      fileHash,
      fileSignature,
      libraryId: job.libraryId,
    },
  });

  if (invokeError) throw invokeError;
  if (!data) throw new Error("Upload service returned no response.");

  let storageKey = data.newKey;

  if (data.alreadyExists) {
    storageKey = data.existingKey;
    await updateStoredJob(job.id, (current) => ({
      ...current,
      progress: 100,
      updatedAt: new Date().toISOString(),
    }));
    await emitQueueUpdate();
  } else {
    if (!data.uploadUrl) {
      throw new Error("Upload service did not provide a destination URL.");
    }

    const exactContentType = data.contentType || uploadSource.type;

    await uploadWithProgress(job.id, data.uploadUrl, uploadSource, exactContentType, async (progress) => {
      await updateStoredJob(job.id, (current) => ({
        ...current,
        progress,
        updatedAt: new Date().toISOString(),
      }));
      await emitQueueUpdate();
    });
  }

  if (!data.alreadyExists) {
    const isVideo = uploadSource.type.startsWith("video/");
    const { error: dbError } = await supabase
      .from("library_assets" as never)
      .insert({
        library_id: job.libraryId,
        asset_type: isImage ? "image" : isVideo ? "video" : "pdf",
        storage_key: storageKey,
        file_name: uploadSource.name,
        mime_type: uploadSource.type,
        size_bytes: uploadSource.size,
        status: "active",
        file_hash: fileHash,
      } as never);

    if (dbError) throw dbError;

    if (uploadSource !== job.file) {
      const savedKB = Math.max(1, Math.round((job.file.size - uploadSource.size) / 1024));
      toast.info(`Image optimized before upload (${savedKB} KB saved).`);
    }

    if (isVideo && job.compressionMode === "standard") {
      toast.info("Video compression was requested. Backend compression still needs to be enabled separately.");
    }
  }

  toast.success(data.alreadyExists ? `Skipped duplicate ${job.fileName}` : `Uploaded ${job.fileName}`);
};

const normalizeJobsAfterRestart = async () => {
  const jobs = await listStoredJobs();
  const staleJobs = jobs.filter((job) => job.status === "processing");
  if (!staleJobs.length) return;

  await Promise.all(
    staleJobs.map((job) =>
      putStoredJob({
        ...job,
        status: "queued",
        progress: Math.min(job.progress, 6),
        updatedAt: new Date().toISOString(),
        errorMessage: null,
      }),
    ),
  );
  await emitQueueUpdate();
};

export const getBackgroundUploadQueueEventName = () => UPLOAD_QUEUE_EVENT;
export const getBackgroundUploadSyncTag = () => BACKGROUND_UPLOAD_SYNC_TAG;
export const getBackgroundUploadServiceWorkerMessage = () => SERVICE_WORKER_PROCESS_UPLOADS_MESSAGE;

export const getBackgroundUploadQueueSnapshot = async () => listPublicJobs();

export const enqueueBackgroundUpload = async ({
  file,
  libraryId,
  compressionMode = "none",
}: {
  file: File;
  libraryId: string;
  compressionMode?: BackgroundUploadCompressionMode;
}) => {
  const now = new Date().toISOString();
  const job: StoredBackgroundUploadJob = {
    id: getSafeRandomId(),
    libraryId,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
    compressionMode,
    status: "queued",
    progress: 0,
    createdAt: now,
    updatedAt: now,
    attemptCount: 0,
    errorMessage: null,
    file,
  };

  await putStoredJob(job);
  await emitQueueUpdate();
  void registerBackgroundSync();
  return toPublicJob(job);
};

export const cancelBackgroundUpload = async (jobId: string) => {
  // Abort the XHR request if it's currently active
  const xhr = activeUploadXhrMap.get(jobId);
  if (xhr) {
    xhr.abort();
    activeUploadXhrMap.delete(jobId);
  }

  const job = await getStoredJob(jobId);
  if (!job) return;

  await updateStoredJob(jobId, (current) => ({
    ...current,
    status: "cancelled",
    updatedAt: new Date().toISOString(),
    errorMessage: "Upload cancelled by user",
  }));

  await emitQueueUpdate();

  // Also clean it up after a brief delay so UI has time to reflect cancelled state before disappearing
  setTimeout(async () => {
    await deleteStoredJob(jobId);
    await emitQueueUpdate();
  }, 2000);
};

export const processBackgroundUploadQueue = async () => {
  if (typeof window === "undefined") return;
  if (activeProcessor) return activeProcessor;

  activeProcessor = (async () => {
    await normalizeJobsAfterRestart();

    while (navigator.onLine) {
      const jobs = await listStoredJobs();
      const nextJob = jobs.find((job) => job.status === "queued" || job.status === "failed");
      if (!nextJob) break;

      await putStoredJob({
        ...nextJob,
        status: "processing",
        progress: Math.max(nextJob.progress, 3),
        updatedAt: new Date().toISOString(),
        attemptCount: nextJob.attemptCount + 1,
        errorMessage: null,
      });
      await emitQueueUpdate();

      try {
        const currentJob = await getStoredJob(nextJob.id);
        if (!currentJob) continue;
        await processSingleUpload(currentJob);
        await deleteStoredJob(nextJob.id);
        await emitQueueUpdate({ completedJobId: nextJob.id });
      } catch (error) {
        if (!navigator.onLine) {
          await updateStoredJob(nextJob.id, (current) => ({
            ...current,
            status: "queued",
            progress: Math.min(current.progress, 10),
            updatedAt: new Date().toISOString(),
          }));
          await emitQueueUpdate();
          void registerBackgroundSync();
          break;
        }

        const functionError = await getFunctionErrorDetails(error);
        const message = functionError || (error instanceof Error ? error.message : "Upload failed");
        await updateStoredJob(nextJob.id, (current) => ({
          ...current,
          status: "failed",
          progress: 0,
          updatedAt: new Date().toISOString(),
          errorMessage: message,
        }));
        await emitQueueUpdate({ failedJobId: nextJob.id });
        void registerBackgroundSync();
        toast.error(`Upload failed: ${message}`);
        break;
      }
    }
  })().finally(() => {
    activeProcessor = null;
  });

  return activeProcessor;
};
